import { Curve, Rectangle, Cross } from 'recharts';

import { tokens } from '../../../../theme/generated/tokens';
import { useChartContext } from '../../ChartProvider';

type Point = {
  x: number;
  y: number;
};

type CursorProps = {
  top?: number;
  height?: number;
  width?: number;
  bottom?: number;
  left?: number;
  x?: number;
  y?: number;
  points?: Point[];
};

const CustomBarCursor = ({ points: _, ...rest }: CursorProps) => {
  const { barSize } = useChartContext();

  // When a fixed barSize is set, center the cursor rect over the bar instead of
  // filling the full category band width that recharts injects.
  // Recharts injects `x` (left edge of the band) and `width` (= tooltipAxisBandSize).
  // Use the same integer-truncated offset that recharts applies to bar positions
  // (pos.offset = (bandSize - barSize) / 2 >> 0) so cursor and bar align pixel-perfectly.
  const centeredProps =
    typeof barSize === 'number' &&
    barSize !== undefined &&
    rest.width !== undefined &&
    rest.x !== undefined
      ? {
          ...rest,
          x: rest.x + Math.floor((rest.width - barSize) / 2),
          width: barSize,
        }
      : rest;

  return (
    <>
      <defs>
        <linearGradient id="bar-cursor" x1=".5" x2=".5" y2="1">
          <stop
            offset="0%"
            stopColor={tokens.theme.bg.structural.surface}
            stopOpacity="0"
          />
          <stop
            offset="100%"
            stopColor={tokens.theme.data.barchartCursor}
            stopOpacity="0.5"
          />
        </linearGradient>
      </defs>

      <Rectangle
        {...centeredProps}
        fill="url(#bar-cursor)"
        stroke="url(#bar-cursor)"
        data-testid="bar-cursor"
      />
    </>
  );
};

const CustomLineAreaCursor = (props: CursorProps) => {
  const points = props.points ?? [
    { x: 0, y: 0 },
    { x: 0, y: 0 },
  ];
  return (
    <g data-testid="line-area-cursor">
      <defs>
        <linearGradient
          id="line-area-cursor"
          x1={points[0].x}
          y1={points[0].y}
          x2={points[1].x}
          y2={points[1].y}
          gradientUnits="userSpaceOnUse"
        >
          <stop
            offset="0.234375"
            stopColor={tokens.theme.data.curverCursor50}
          />
          <stop offset="1" stopColor={tokens.theme.data.curveCursor300} />
        </linearGradient>
      </defs>

      <Curve {...props} stroke="url(#line-area-cursor)" />
    </g>
  );
};

const CustomScatterCursor = ({ points: _, ...rest }: CursorProps) => {
  return (
    <Cross
      {...rest}
      stroke={tokens.theme.border.structural.neutral}
      data-testid="scatter-cursor"
    />
  );
};

export const CustomCursor = (props: CursorProps) => {
  const { chartType } = useChartContext();

  switch (chartType) {
    case 'bar':
      return <CustomBarCursor {...props} />;
    case 'scatter':
      return <CustomScatterCursor {...props} />;
    default:
      return <CustomLineAreaCursor {...props} />;
  }
};
